import React, { useEffect, useState } from "react";
import { Cpu, RefreshCw, Activity } from "lucide-react";
import { COLOR } from "./pillars/_cards";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * FirmwareTrackerCard — live Tesla firmware history for the current VIN.
 *
 * Backend endpoint /api/firmware/{vin} accumulates one document per
 * (vin, car_version) seen through the Fleet API. This card renders:
 *   • Current version + when it first showed up on the car
 *   • Count of prior versions (updates observed)
 *   • Compact history list for expand
 *
 * The card lives on CareView so owners can see their update cadence
 * next to service intervals and warranty status.
 */
export const FirmwareTrackerCard = ({ vehicle }) => {
  const vin = vehicle?.vin || null;
  const [data, setData] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    if (!vin) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE}/api/firmware/${encodeURIComponent(vin)}`),
        fetch(`${API_BASE}/api/firmware/${encodeURIComponent(vin)}/efficiency`),
      ]);
      if (r1.ok) setData(await r1.json());
      if (r2.ok) setEfficiency(await r2.json());
    } catch (_e) {
      /* offline */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setEfficiency(null);
    setExpanded(false);
    if (vin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vin]);

  const currentVersion =
    data?.current?.version || vehicle?.firmwareVersion || null;
  const updates = data?.updates ?? 0;
  const first = data?.current?.first_seen
    ? new Date(data.current.first_seen).toLocaleDateString()
    : null;
  const currentEff = (efficiency?.by_version || []).find(
    (e) => e.version === currentVersion && e.delta_wh_per_mi != null
  );
  const effColor = currentEff
    ? currentEff.delta_wh_per_mi < 0 ? COLOR.green
      : currentEff.delta_wh_per_mi > 0 ? COLOR.amber : COLOR.zinc
    : null;

  return (
    <div
      className="rounded-xl border p-3"
      style={{
        borderColor: "rgba(0,229,255,0.28)",
        background: "linear-gradient(135deg, rgba(0,229,255,0.04), rgba(255,255,255,0.02))",
      }}
      data-testid="care-firmware-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.14)", borderColor: "rgba(0,229,255,0.45)" }}
        >
          <Cpu className="w-4 h-4" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Firmware
          </div>
          <div className="text-[13px] text-white font-semibold leading-tight" data-testid="care-firmware-current">
            {currentVersion || "Awaiting sync…"}
          </div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            {first ? `Installed ${first}` : "First-seen not yet recorded"}
            {updates > 0 && (
              <>
                {" · "}
                <span data-testid="care-firmware-updates">
                  {updates} prior {updates === 1 ? "version" : "versions"}
                </span>
              </>
            )}
          </div>
          {currentEff && (
            <div
              className="font-mono text-[10px] leading-tight mt-0.5 flex items-center gap-1"
              style={{ color: effColor }}
              data-testid="care-firmware-impact"
            >
              <Activity className="w-2.5 h-2.5" strokeWidth={2.6} />
              This update: {currentEff.delta_wh_per_mi > 0 ? "+" : ""}
              {currentEff.delta_wh_per_mi} Wh/mi vs prior
              {currentEff.delta_wh_per_mi < 0 ? " · more efficient" :
               currentEff.delta_wh_per_mi > 0 ? " · uses more energy" : ""}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading || !vin}
            aria-label="Refresh firmware history"
            data-testid="care-firmware-refresh"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} />
          </button>
          {data?.history?.length > 1 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="care-firmware-toggle"
              className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              {expanded ? "Hide" : "History"}
            </button>
          )}
        </div>
      </div>

      {expanded && data?.history?.length > 1 && (
        <div className="mt-3 border-t border-white/5 pt-2 space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {data.history.map((h, i) => {
            const eff = (efficiency?.by_version || []).find((e) => e.version === h.version);
            const deltaColor =
              !eff || eff.delta_wh_per_mi == null ? COLOR.zinc :
              eff.delta_wh_per_mi < 0 ? COLOR.green :
              eff.delta_wh_per_mi > 0 ? COLOR.amber : COLOR.zinc;
            return (
              <div
                key={`${h.version}-${i}`}
                className="text-[11px] leading-snug"
                data-testid={`care-firmware-history-item-${i}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-white">{h.version}</span>
                    {i === 0 && (
                      <span
                        className="ml-2 font-mono text-[9px] uppercase tracking-wider px-1 py-[1px] rounded"
                        style={{ background: `${COLOR.green}14`, color: COLOR.green }}
                      >
                        current
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 shrink-0">
                    {h.first_seen ? new Date(h.first_seen).toLocaleDateString() : "—"}
                    {h.miles_on_version != null && (
                      <> · {h.miles_on_version.toLocaleString()} mi</>
                    )}
                  </div>
                </div>
                {eff && eff.avg_wh_per_mi != null && (
                  <div className="pl-1 mt-0.5 flex items-center gap-1.5 text-[10px] font-mono text-zinc-500" data-testid={`care-firmware-efficiency-${i}`}>
                    <Activity className="w-2.5 h-2.5" strokeWidth={2.6} style={{ color: deltaColor }} />
                    <span style={{ color: deltaColor }}>
                      {eff.avg_wh_per_mi} Wh/mi
                    </span>
                    {eff.delta_wh_per_mi != null && (
                      <span style={{ color: deltaColor }}>
                        · {eff.delta_wh_per_mi > 0 ? "+" : ""}{eff.delta_wh_per_mi} vs prior
                      </span>
                    )}
                    <span className="text-zinc-600">· {eff.samples} pairs</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
